import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLazyFetchPathNormativitiesQuery } from "../../../../api/rtk/base/slices/diagnosticsApiSlice";
import { getItemWebStorage } from "../../../../features/webStorageSaver";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";
import { setPathNormativitiesForMapTooltip } from "../../../../stateManagement/reducers/diagnostics/PathNormativitiesSlice";
import { setMapTooltipIsVisible } from "../../../../stateManagement/reducers/MapTooltipSlice";
import { DiagnosticsYearFilters } from "../../../../widgets/diagnosticsYearFilters";
import { MapTooltip } from "../../../../widgets/map/mapAddons/mapTooltip";
import { getBasicObjectTypeLayer, getAllObjectsLayer } from "../../../../widgets/map/mapFeatures/layers";
import { copyInfoFromTooltip, showMapTooltip } from "../../../../widgets/map/mapFeatures/mapFunctions/showMapTooltip/showMapTooltip";
import MapWidget from "../../../../widgets/map/mapWidget/MapWidget";
import { mapLayersDict } from "../../mapLayersDict";
import { setHighContrastModeIsEnabled } from "../../../../stateManagement/reducers/ContentHeaderSlice";
import ContentHeader from "../../../../widgets/contentHeader/ContentHeader";

export function MapPathNormativitiesMeasureTypesGroup() {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const objectTypeName = "PathNormativitiesMeasureTypesGroup"
    const [isSmallWindow, setIsSmallWindow] = useState(false);
    const [mapTitle, setMapTitle] = useState<string>('');
    const [additionalLayers, setAdditionalLayers] = useState<any>([]);
    const sideBarState = useAppSelector(state => state.sidebar.sidebarIsShown)
    const { pathNormativitiesMapYearFilter: yearFilter,
        pathNormativitiesMapMultipleMeasureTypes: measureTypes,
        pathNormativitiesMapNormativityFlag: normativityFlag,
        pathNormativitiesMapUnionMode: unionMode
    } = useAppSelector(state => state.diagnosticsPathNormativities)
    const { wmsServer, wmsVersion, workspace } = getItemWebStorage('currentConfig')
    const [fetchPathNormativitiesInfo] = useLazyFetchPathNormativitiesQuery()

    const roadsLayer = getAllObjectsLayer()
    const [permanentLayers] = useState([roadsLayer])

    useEffect(() => {
        const { mapLayer, caption } = mapLayersDict[objectTypeName] ?? {}
        caption && setMapTitle(`Карта «${caption} (По нескольким показателям)»`)
        let measureTypesFilterString = Object.entries(measureTypes)
            .filter(([key, value]) => value === true)
            .map(([key, value]) => `${key}=${normativityFlag ? 1 : 0}`)
            .join(unionMode === 'intersection' ? ' and ' : ' or ');
        if (unionMode === 'union') {
            measureTypesFilterString = `(${measureTypesFilterString})`
        }
        let layerFilter = `DiagnosticYear in (${yearFilter}) and ${measureTypesFilterString}`;
        const style = normativityFlag ? 'MADiagSegmentNormativityAllGreens' : 'MADiagSegmentNormativityAllReds'
        const objectTypeLayer = getBasicObjectTypeLayer(mapLayer, wmsServer, wmsVersion, workspace, layerFilter, style)
        setAdditionalLayers([objectTypeLayer])
        dispatch(setHighContrastModeIsEnabled(true))
    }, [yearFilter, measureTypes, normativityFlag, unionMode]);

    // при изменении года запрашивать участки нормативности для подсказки
    useEffect(() => {
        getPathNormativitiesForCurrentYear()
    }, [yearFilter])

    useEffect(() => {
        const contentHeaderWidth: any = document.querySelector('.content-header-div')?.getBoundingClientRect().width;
        setIsSmallWindow(contentHeaderWidth < 1381);
    }, [sideBarState, navigate])

    useEffect(() => {
        const contentHeaderWidth: any = document.querySelector('.content-header-div')?.getBoundingClientRect().width;
        const hasSeenYearForMobile = getItemWebStorage('diagnosticsYearFilters')?.pathNormativitiesMapMeasureTypeGroupsSeen
        if (!hasSeenYearForMobile && contentHeaderWidth < 1381) {
            navigate('/app/map/PathNormativities/group/filters')
        }
    }, [])

    // функция для запроса участков нормативности для подсказки
    async function getPathNormativitiesForCurrentYear() {
        let filterStringArray = [`DiagnosticYear eq ${yearFilter}`]
        const response = await fetchPathNormativitiesInfo({ filterStringArray })
        if (response?.data?.value) {
            dispatch(setPathNormativitiesForMapTooltip(response.data.value))
        }
    }

    function applyMapTooltip(map: any) {
        const pointerMoveFunc = (event: any) => showMapTooltip(event, 'pathNormativities', additionalLayers[0], map)
        const activeTooltipClick = (event: any) => copyInfoFromTooltip()
        map?.on("pointermove", pointerMoveFunc)
        map?.on("singleclick", activeTooltipClick)
        return {
            off: () => {
                map?.un("pointermove", pointerMoveFunc)
                map?.un("singleclick", activeTooltipClick)
                dispatch(setMapTooltipIsVisible(false))
            }
        }
    }

    const mapEventHandlers = [
        {
            name: "mapTooltip",
            func: applyMapTooltip
        }
    ]

    return (
        <>
            <ContentHeader title={mapTitle} objectTypeName={objectTypeName} type="map" option="pathNormativitiesMeasureTypesGroup" />
            {!isSmallWindow && <DiagnosticsYearFilters type="header" filterType="PathNormativitiesMapMeasureTypesGroup" />}
            <div className={`map-widget-with-toast${!isSmallWindow ? ' with-year-filter-row' : ''}`}>
                <MapTooltip type={objectTypeName} />
                <MapWidget additionalLayers={additionalLayers} permanentLayers={permanentLayers} mapEventHandlers={mapEventHandlers} />
            </div>
        </>
    )
}
